import React from 'react';
import { Eye, EyeOff, Loader2 } from 'lucide-react';
import { Link, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useTheme } from '../../contexts/ThemeContext';
import useRegisterForm from '../../hooks/useRegisterForm';
import SiteLogo from '../../components/SiteLogo';

const RegisterClassic = () => {
  const { t } = useTranslation('auth');
  const { isDark } = useTheme();
  const {
    username, email, password,
    showPassword, loading, error, validationErrors,
    showCookieNotice,
    setShowPassword,
    handleUsernameChange,
    handleEmailChange, handlePasswordChange,
    handleSubmit,
    handleAcceptAll, handleOnlyNecessary,
    socialProviders, handleSocialLogin,
  } = useRegisterForm();

  const [searchParams] = useSearchParams();
  const returnTo = searchParams.get('returnTo');
  const loginUrl = returnTo ? `/login?returnTo=${encodeURIComponent(returnTo)}` : '/login';

  const inputClass = (hasError) =>
    `w-full h-[52px] px-4 bg-gray-100 dark:bg-white/[0.05] border rounded-xl text-gray-900 dark:text-white text-[15px] placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:border-gray-400 dark:focus:border-white/30 focus:bg-gray-50 dark:focus:bg-white/[0.07] transition-all duration-200 ${
      hasError ? 'border-red-500/50' : 'border-gray-200 dark:border-white/10'
    }`;

  const socialBtnClass = 'w-full flex items-center justify-center gap-3 h-[52px] bg-transparent border border-gray-200 dark:border-white/15 rounded-xl text-gray-700 dark:text-white text-[15px] font-medium hover:bg-gray-50 dark:hover:bg-white/[0.04] hover:border-gray-300 dark:hover:border-white/25 transition-all duration-200';

  return (
    <div className="min-h-screen bg-white dark:bg-[#0a0a0a] flex flex-col items-center relative overflow-hidden">
      {/* Gradient glow */}
      <div
        className="absolute top-0 left-0 w-[600px] h-[600px] rounded-full opacity-10 dark:opacity-30 blur-[120px] pointer-events-none"
        style={{ background: `radial-gradient(circle, var(--color-primary, #10b981) 0%, transparent 70%)` }}
      />
      <div
        className="absolute bottom-0 right-0 w-[400px] h-[400px] rounded-full opacity-5 dark:opacity-15 blur-[100px] pointer-events-none"
        style={{ background: `radial-gradient(circle, var(--color-primary, #10b981) 0%, transparent 70%)` }}
      />

      {/* Logo */}
      <div className="w-full max-w-[480px] px-6 pt-12 sm:pt-16 pb-8 flex justify-center relative z-10">
        <Link to="/">
          <SiteLogo className="h-9 sm:h-10" />
        </Link>
      </div>

      {/* Form container */}
      <main className="w-full max-w-[480px] px-6 pb-12 relative z-10">
        {/* Social signup */}
        {socialProviders.length > 0 && (
          <>
            <div className="space-y-3">
              {socialProviders.includes('google') && (
                <button type="button" onClick={() => handleSocialLogin('google')} className={socialBtnClass}>
                  <svg className="w-5 h-5" viewBox="0 0 24 24">
                    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 01-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" />
                    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
                    <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
                    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
                  </svg>
                  {t('register.signUpWithGoogle', 'Continue with Google')}
                </button>
              )}
              {socialProviders.includes('twitter') && (
                <button type="button" onClick={() => handleSocialLogin('twitter')} className={socialBtnClass}>
                  <svg className="w-[18px] h-[18px]" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
                  </svg>
                  {t('register.signUpWithX', 'Continue with X')}
                </button>
              )}
              {socialProviders.includes('facebook') && (
                <button type="button" onClick={() => handleSocialLogin('facebook')} className={socialBtnClass}>
                  <svg className="w-5 h-5" fill="#1877F2" viewBox="0 0 24 24">
                    <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
                  </svg>
                  {t('register.signUpWithFacebook', 'Continue with Facebook')}
                </button>
              )}
            </div>

            {/* Divider */}
            <div className="relative my-7">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-gray-200 dark:border-white/10" />
              </div>
              <div className="relative flex justify-center">
                <span className="bg-white dark:bg-[#0a0a0a] px-4 text-sm text-gray-400 dark:text-gray-500">
                  {t('register.orUseEmail', 'or')}
                </span>
              </div>
            </div>
          </>
        )}

        {/* Form */}
        <form onSubmit={handleSubmit} className="space-y-4">
          {error && (
            <div className="p-3.5 bg-red-50 dark:bg-red-500/10 border border-red-200 dark:border-red-500/20 rounded-xl text-red-600 dark:text-red-400 text-sm">
              {error}
            </div>
          )}

          {/* Username */}
          <div>
            <input
              type="text"
              value={username}
              onChange={(e) => handleUsernameChange(e.target.value)}
              placeholder={t('register.usernamePlaceholder', 'Username')}
              className={inputClass(validationErrors.username)}
              required
            />
            {validationErrors.username && (
              <p className="text-red-500 dark:text-red-400 text-xs mt-1.5 ps-1">{validationErrors.username}</p>
            )}
          </div>

          {/* Email */}
          <div>
            <input
              type="email"
              value={email}
              onChange={(e) => handleEmailChange(e.target.value)}
              placeholder={t('register.emailPlaceholderAlt', 'Email')}
              className={inputClass(validationErrors.email)}
              required
            />
            {validationErrors.email && (
              <p className="text-red-500 dark:text-red-400 text-xs mt-1.5 ps-1">{validationErrors.email}</p>
            )}
          </div>

          {/* Password */}
          <div>
            <div className="relative">
              <input
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => handlePasswordChange(e.target.value)}
                placeholder={t('register.passwordPlaceholder', 'Password')}
                className={`${inputClass(validationErrors.password)} pe-12`}
                required
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute end-4 top-1/2 -translate-y-1/2 text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
              >
                {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
            </div>
            {validationErrors.password && (
              <p className="text-red-500 dark:text-red-400 text-xs mt-1.5 ps-1">{validationErrors.password}</p>
            )}
          </div>

          {/* Terms notice */}
          <p className="text-xs text-gray-500 leading-relaxed ps-1">
            {t('register.termsAgreement', 'By signing up, you agree to our')}{' '}
            <a href="/page/terms-of-service" className="text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white underline transition-colors">
              {t('register.termsOfService', 'Terms')}
            </a>
            {' & '}
            <a href="/page/privacy-policy" className="text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white underline transition-colors">
              {t('register.privacyPolicy', 'Privacy Policy')}
            </a>
          </p>

          {/* Sign Up */}
          <button
            type="submit"
            disabled={loading}
            className="w-full h-[52px] bg-gray-900 dark:bg-white hover:bg-gray-800 dark:hover:bg-gray-100 active:bg-gray-700 dark:active:bg-gray-200 text-white dark:text-black rounded-full font-semibold text-[15px] transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 mt-2"
          >
            {loading ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                {t('register.creatingAccount', 'Creating account...')}
              </>
            ) : (
              t('register.createAccount', 'Sign Up')
            )}
          </button>
        </form>

        {/* Bottom section — login + legal */}
        <div className="mt-10 pt-8 border-t border-gray-100 dark:border-white/[0.06]">
          <p className="text-center text-sm text-gray-500 dark:text-gray-400 mb-4">
            {t('register.alreadyHaveAccount', 'Already have an account?')}
          </p>
          <Link
            to={loginUrl}
            className="block w-full h-[48px] border border-gray-300 dark:border-white/15 text-gray-700 dark:text-white rounded-full font-semibold text-[14px] transition-all duration-200 flex items-center justify-center hover:bg-gray-50 dark:hover:bg-white/[0.04] hover:border-gray-400 dark:hover:border-white/25"
          >
            {t('register.logInLink', 'Sign in')}
          </Link>
          <div className="flex items-center justify-center gap-3 mt-6">
            <a href="/page/terms-of-service" className="text-[11px] text-gray-400 dark:text-gray-600 hover:text-gray-600 dark:hover:text-gray-400 transition-colors">
              {t('register.termsOfService', 'Terms')}
            </a>
            <span className="text-gray-300 dark:text-gray-700 text-[11px]">&middot;</span>
            <a href="/page/privacy-policy" className="text-[11px] text-gray-400 dark:text-gray-600 hover:text-gray-600 dark:hover:text-gray-400 transition-colors">
              {t('register.privacyPolicy', 'Privacy')}
            </a>
            <span className="text-gray-300 dark:text-gray-700 text-[11px]">&middot;</span>
            <a href="/page/cookie-policy" className="text-[11px] text-gray-400 dark:text-gray-600 hover:text-gray-600 dark:hover:text-gray-400 transition-colors">
              {t('register.cookiePolicy', 'Cookies')}
            </a>
          </div>
        </div>
      </main>

      {/* Cookie Notice */}
      {showCookieNotice && (
        <div className="fixed bottom-0 start-0 end-0 z-50 bg-white dark:bg-[#1a1a1a] border-t border-gray-200 dark:border-white/[0.06]">
          <div className="max-w-xl mx-auto px-5 py-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <p className="text-sm text-gray-500 dark:text-gray-400">
              {t('login.cookieMessage')}{' '}
              <a href="/page/cookie-policy" className="text-gray-900 dark:text-white font-medium hover:underline">{t('login.cookieLearnMore')}</a>
            </p>
            <div className="flex gap-2 shrink-0">
              <button onClick={handleOnlyNecessary} className="px-4 py-2 text-sm font-medium text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-white/[0.06] rounded-lg transition-colors">
                {t('login.necessaryOnly')}
              </button>
              <button onClick={handleAcceptAll} className="px-4 py-2 text-sm font-medium text-white dark:text-black bg-gray-900 dark:bg-white hover:bg-gray-800 dark:hover:bg-gray-200 rounded-lg transition-colors">
                {t('login.acceptAll')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RegisterClassic;
